<script setup lang="ts">
const { t } = useI18n({ useScope: "local" });
const props = defineProps<{ id: string; defaultOpen?: boolean }>();
const { getContentById, updateContentById } = useContentManager();

const mockDataOpen = ref(false);
const draftMockData = ref("[]");
const parseError = ref<string | null>(null);
const isFullscreen = ref(false);
const monacoOptions = {
  automaticLayout: true,
  wordWrap: "on",
  minimap: { enabled: false },
};

const drawerWidthClass = computed(() =>
  isFullscreen.value
    ? "data-[vaul-drawer-direction=right]:w-screen data-[vaul-drawer-direction=right]:sm:max-w-none"
    : "data-[vaul-drawer-direction=right]:w-[65vw] data-[vaul-drawer-direction=right]:sm:max-w-[1080px]"
);

function openMockData() {
  const current = getContentById(props.id)?.mockData;
  draftMockData.value = typeof current === "string" && current.trim() ? current : "[]";
  parseError.value = null;
  isFullscreen.value = false;
  mockDataOpen.value = true;
}

function saveMockData() {
  try {
    JSON.parse(draftMockData.value);
  } catch (error) {
    parseError.value = error instanceof Error ? error.message : t("invalidJson");
    return;
  }

  updateContentById(props.id, { mockData: draftMockData.value });
  mockDataOpen.value = false;
}
</script>

<template>
  <EditorSection :title="t('title')" icon="lucide:braces" accent="reporting" :default-open="defaultOpen ?? true">
    <EditorField :label="t('mockDataLabel')" :hint="t('mockDataHint')">
      <Button variant="outline" size="sm" class="w-full justify-start rounded-sm" @click="openMockData">
        <Icon mode="svg" size="15" name="lucide:braces" class="mr-2 text-muted-foreground" />
        {{ t("editMockData") }}
      </Button>
    </EditorField>

    <Drawer v-model:open="mockDataOpen" direction="right">
      <DrawerContent :class="['flex h-full flex-col transition-[width] duration-200', drawerWidthClass]">
        <DrawerHeader class="flex flex-row items-start justify-between">
          <div>
            <DrawerTitle>{{ t("mockDataTitle") }}</DrawerTitle>
            <DrawerDescription>{{ t("mockDataDescription") }}</DrawerDescription>
          </div>
          <Button variant="ghost" size="icon" class="size-8 shrink-0" @click="isFullscreen = !isFullscreen">
            <Icon mode="svg" size="16" :name="isFullscreen ? 'lucide:minimize-2' : 'lucide:maximize-2'" />
          </Button>
        </DrawerHeader>
        <div class="flex-1 overflow-hidden px-4">
          <ClientOnly>
            <MonacoEditor
              v-model="draftMockData"
              lang="json"
              :options="monacoOptions"
              class="h-full w-full rounded-sm border"
            />
          </ClientOnly>
          <p v-if="parseError" class="mt-2 text-sm text-destructive">{{ parseError }}</p>
        </div>
        <DrawerFooter>
          <Button @click="saveMockData">{{ t("save") }}</Button>
          <DrawerClose as-child>
            <Button variant="outline">{{ t("cancel") }}</Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  </EditorSection>
</template>

<i18n lang="yaml">
en:
  title: "Mock Report Provider"
  mockDataLabel: "Mock Data"
  mockDataHint: "Paste valid JSON to provide it unchanged to child blocks."
  editMockData: "Edit mock data"
  mockDataTitle: "Edit Mock Report Data"
  mockDataDescription: "This JSON is provided directly to the report data contract without any API request or mapping."
  invalidJson: "Enter valid JSON before saving."
  save: "Save"
  cancel: "Cancel"
</i18n>
